import { type EntityState } from '@ngrx/entity';
import { current, type Draft } from 'immer';

import { type EngineStateBase } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type CanvasElementId,
  type ColumnId,
  type PropsOf,
  type SlotId,
  type Slots,
} from '@blaze-runtime/engine-domain';
import { type CanvasElementDataProperties } from '@blaze-shared/elements-domain';
import { isEmptyish } from '@blaze-shared/shared-util';

import { sanitizeNavMenusAfterElementRemoveMutably } from '../../+state/nav-menu/nav-menu.reducer.utils';
import { sanitizeConnectionsAndDataAfterUiChangeMutably } from '../../+state/runtime/runtime.reducer.utils';
import { arePropsWithVisibility } from '../../canvas-elements/element-model/are-props-with-visibility';
import { hasDataProps, hasSlots } from '../../canvas-elements/element-model/element-props-guards';
import {
  isDataFilter,
  isElementType,
  isVariantOfButton,
  isVariantOfContainer,
} from '../../canvas-elements/element-model/element-type-guards';
import { getElementSlotsCount } from '../../canvas-elements/element-model/slots';
import { sanitizeButtonConnectionsMutably } from '../runtime/connection';

/**
 * This will remove the element from the state, including all its children.
 * It will skip sanitization of connections and data. This is used for operations which take place outside of the reducer, is deprecated and should be removed in the future once all usages are removed.
 * @deprecated
 */
export function removeElementMutablyWithoutSanitization(
  draft: Draft<EntityState<CanvasElement>>,
  elementId: CanvasElementId,
): void {
  const element = draft.entities[elementId];

  // Recursively remove all children nested inside slots
  if (hasSlots(element)) {
    const slots = element?.slots;

    slots?.ids.forEach((slotId) => {
      slots[slotId]?.children.forEach((childElementId) => {
        removeElementMutablyWithoutSanitization(draft, childElementId);
      });
    });
  }

  removeElementFromParentMutably(draft, elementId);
  draft.ids = (draft.ids as CanvasElementId[]).filter((id) => id !== elementId);
  delete draft.entities[elementId];
}

/**
 * Note: this function should be run inside of immer's `produce` to ensure immutability
 */
export function removeElementMutably(
  draft: Draft<EngineStateBase>,
  elementId: CanvasElementId,
  depth = 0,
): void {
  const element = draft.entities[elementId];

  // Recursively remove all children nested inside slots
  if (hasSlots(element)) {
    const slots = element?.slots;

    slots?.ids.forEach((slotId) => {
      slots[slotId]?.children.forEach((childElementId) => {
        removeElementMutably(draft, childElementId, depth + 1);
      });
    });
  }

  removeElementFromParentMutably(draft, elementId);
  draft.ids = (draft.ids as CanvasElementId[]).filter((id) => id !== elementId);
  delete draft.entities[elementId];

  // If it's a button (workflow blocking element), find workflow connected to the workflow and remove all input
  // connections to that workflow.
  if (isVariantOfButton(element)) {
    sanitizeButtonConnectionsMutably(draft, element.id);
  }

  if (isElementType(element, 'navMenu')) {
    sanitizeNavMenusAfterElementRemoveMutably(draft, element);
  }

  if (isDataFilter(element)) {
    removeGlobalQueryMutably(draft, elementId);
  }

  // If it's the top-level call (all nested calls were already processed)
  // sanitize connections and data
  if (depth === 0) {
    sanitizeConnectionsAndDataAfterUiChangeMutably(draft);
  }
}

export function deleteSlotMutably(
  draft: Draft<EngineStateBase>,
  elementId: CanvasElementId,
  index: number,
): void {
  const element = draft.entities[elementId];
  if (!element?.slots) {
    throw new Error('Could not find element / no slots');
  }

  const slotId = element.slots.ids[index];

  // Remove all slot children
  element.slots[slotId]?.children.forEach((childElementId) => {
    removeElementMutably(draft, childElementId);
  });

  // Remove slot from list
  element.slots.ids = element.slots.ids.filter((id) => id !== slotId);

  delete element.slots[slotId];
}

/**
 * Note: this function should be run inside of immer's `produce` to ensure immutability
 */
export function removeColumnMutably(
  draft: Draft<EngineStateBase>,
  elementId: CanvasElementId,
  slotId: ColumnId,
): void {
  const element = draft.entities[elementId];

  // Check if element is a container - BL-6415
  if (!isVariantOfContainer(element)) {
    console.error('Trying to remove column from non-container element');
    return;
  }

  // Recursively remove all children and nested
  const slots = element.slots;

  if (!slots?.ids.includes(slotId)) {
    throw new Error(`Element id '${elementId}' is missing slot '${slotId}'`);
  }

  // Remove all slot children
  slots[slotId]?.children.forEach((childElementId) => {
    removeElementMutably(draft, childElementId);
  });

  passColumnWidthToSiblingMutably(slots, slotId);

  // Remove slot from list
  slots.ids = slots.ids.filter((id) => id !== slotId);

  delete slots[slotId];

  moveVisibilityFromSlotToContainerMutably(draft, elementId);

  // If it was the last slot - remove element
  if (slots.ids.length === 0) {
    removeElementMutably(draft, elementId);
  }
}

/**
 * Note: this function should be run inside of immer's `produce` to ensure immutability
 */
export function removeElementFromParentMutably(
  draft: Draft<EntityState<CanvasElement>>,
  elementId: CanvasElementId,
): void {
  const element = draft.entities[elementId];

  if (
    !element ||
    !element.parentNode ||
    element.parentNode.id === undefined ||
    element.parentNode.slot === undefined
  ) {
    console.warn(`Element id: ${elementId} could not be identified correctly`);
    return;
  }

  const parentElement = draft.entities[element.parentNode.id];
  if (hasSlots(parentElement)) {
    const slot = parentElement.slots[element.parentNode.slot];
    if (!slot) {
      throw new Error('Could not find slot');
    }

    slot.children = slot.children.filter((id) => id !== elementId);
    element.parentNode = undefined;
  }
}

/**
 * Note: this function should be run inside of immer's `produce` to ensure immutability
 */
// TODO: test
export function passColumnWidthToSiblingMutably(
  slots: Draft<Slots<SlotId>>,
  removedSlotId: SlotId,
): void {
  const sizing = slots[removedSlotId]?.sizing;

  // Find neighbour
  const removedSlotIndex = slots.ids.indexOf(removedSlotId);
  if (removedSlotIndex === -1) {
    return;
  }

  const siblingSlotId = slots.ids[removedSlotIndex + 1] || slots.ids[removedSlotIndex - 1];

  // Not enough neighbours
  if (!siblingSlotId) {
    return;
  }

  const siblingSlot = slots[siblingSlotId];
  if (!siblingSlot) {
    throw new Error('Could not find sibling slot');
  }

  // Pass the width
  if (!sizing || sizing?.mode === 'auto' || sizing?.mode === 'fit-content') {
    siblingSlot.sizing = { mode: 'auto' };
  } else if (sizing?.mode === 'percent' && siblingSlot.sizing?.mode === 'percent') {
    // If 'percent' column was removed, and neighbour is also percent - sum percents
    siblingSlot.sizing = {
      mode: 'percent',
      width: (sizing.width || 0) + (siblingSlot.sizing?.width || 0),
    };
  } else if (sizing?.mode === 'percent' && siblingSlot.sizing?.mode === 'fit-content') {
    siblingSlot.sizing = { mode: 'auto' };
  }
}

export function removeGlobalQueryMutably(
  draft: Draft<EngineStateBase>,
  elementId: CanvasElementId,
): void {
  const gq = current(draft.globalQuery);
  const elementGlobalQuery = Object.entries(gq).find(
    ([, data]) => data?.elementId === elementId,
  )?.[0];
  if (elementGlobalQuery) {
    delete draft.globalQuery[elementGlobalQuery];
  }
}

/**
 * When deleting last column from container move visibility props to main element.
 * @param draft
 * @param elementId
 */
function moveVisibilityFromSlotToContainerMutably(
  draft: Draft<EngineStateBase>,
  elementId: CanvasElementId,
): void {
  const element = draft.entities[elementId];

  if (!isVariantOfContainer(element)) {
    return;
  }

  const slotsCount = getElementSlotsCount(element);
  if (slotsCount !== 1) {
    return;
  }

  const targetSlotId = element.slots.ids[0];
  if (!targetSlotId) {
    return;
  }

  const slotProps = element.slots[targetSlotId]?.props as PropsOf<'container'> | undefined;
  if (!arePropsWithVisibility(slotProps)) {
    return;
  }

  let anyPropsWereCopied = false;

  // Move viewport visibility
  if (slotProps.content.visible?.viewports) {
    element.props.content = {
      ...element.props?.content,
      visible: {
        states: [],
        conditions: [],
        ...element.props?.content?.visible,
        viewports: slotProps.content.visible.viewports,
      },
    };
    delete slotProps.content.visible.viewports;
    anyPropsWereCopied = true;
  }

  // Move expression visibility
  if (slotProps.content.visibilityExpression) {
    const newVisibilityDataProps: CanvasElementDataProperties = {
      key: 'visible',
      enabled: true,
      dataSource: {
        sourceType: 'expression',
        expression: slotProps?.content?.visibilityExpression ?? [],
      } as any,
    };
    if (hasDataProps(element)) {
      element.props.data = [
        ...(element.props.data ?? []).filter((data) => data.key !== 'visible'),
        newVisibilityDataProps,
      ];
    }
    element.props.content = {
      ...element.props.content,
      visible: {
        states: [],
        conditions: [],
        ...element.props?.content?.visible,
        ...slotProps.content.visible,
      },
    };

    delete slotProps.content.visibilityExpression;
    anyPropsWereCopied = true;
  }

  if (isEmptyish(slotProps.content.visible)) {
    delete slotProps.content.visible;
  }

  if (anyPropsWereCopied) {
    console.log(
      `[Elements] Copied visibility props from slot: ${targetSlotId} to it's element: ${elementId}`,
    );
  }
}
